// src/pages/Clientes.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import GlassCard from '../components/UI/GlassCard';
import Table from '../components/UI/Table';
import Modal from '../components/UI/Modal';
import { 
  UserPlus, 
  Search, 
  Edit2, 
  Trash2, 
  Phone, 
  Mail, 
  MapPin, 
  Dog, 
  ChevronRight,
  RefreshCw,
  Plus,
  Calendar,
  Scale,
  User,
  Eye,
  FileText,
  ArrowRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Swal from 'sweetalert2';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';
import { formatPhone } from '../utils/formatters';

// Componente para evitar el warning de ref en React 19
const SafeTippy = ({ children, ...props }) => {
  const [ref, setRef] = useState(null);
  return (
    <>
      <Tippy {...props} reference={ref} />
      {React.cloneElement(React.Children.only(children), { ref: setRef })}
    </>
  );
};

const Clientes = () => {
    const { token, can } = useAuth();
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Modales
    const [showModal, setShowModal] = useState(false);
    const [showPetModal, setShowPetModal] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    
    // Form State
    const [editingCliente, setEditingCliente] = useState(null);
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        apellido: '',
        email: '',
        telefono: '',
        ciudad: '',
        notes: ''
    });

    const [petFormData, setPetFormData] = useState({
        nombre: '',
        especie_id: '1',
        genero: 'Macho',
        fecha_nacimiento: '',
        peso: ''
    });

    const fetchClientes = useCallback(async () => {
        try {
            const response = await axios.get(`/backend/api/clientes/index.php`);
            if (response.data.success) {
                setClientes(response.data.data);
            }
        } catch (error) {
            toast.error('Error al cargar clientes');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchClientes();
    }, [fetchClientes]);

    const handleEdit = (cliente) => {
        setEditingCliente(cliente);
        setFormData({
            nombre: cliente.nombre,
            apellido: cliente.apellido,
            email: cliente.email,
            telefono: cliente.telefono,
            ciudad: cliente.ciudad || '',
            notes: cliente.notes || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: '¿Eliminar cliente?',
            text: "Esta acción no se puede deshacer y afectará a sus mascotas vinculadas.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#8b5cf6',
            confirmButtonText: 'Sí, eliminar',
            background: 'rgba(15, 23, 42, 0.95)',
            color: '#fff'
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.delete(`/backend/api/clientes/index.php?id=${id}`);
                if (response.data.success) {
                    toast.success('Cliente eliminado');
                    fetchClientes();
                }
            } catch (error) {
                toast.error('Error al eliminar');
            }
        }
    };

    const handleSave = async () => {
        if (!formData.nombre || !formData.apellido || !formData.telefono) {
            return toast.error('Complete los campos obligatorios');
        }

        try {
            const url = '/backend/api/clientes/index.php';
            const method = editingCliente ? 'PUT' : 'POST';
            const data = editingCliente ? { ...formData, id: editingCliente.id } : formData;

            const response = await axios({ method, url, data });

            if (response.data.success) {
                toast.success(editingCliente ? 'Cliente actualizado' : 'Cliente registrado');
                setShowModal(false);
                setEditingCliente(null);
                setFormData({ nombre: '', apellido: '', email: '', telefono: '', ciudad: '', notes: '' });
                fetchClientes();
            }
        } catch (error) {
            toast.error('Error al guardar datos');
        }
    };

    const openPetModal = (cliente) => {
        setSelectedCliente(cliente);
        setPetFormData({
            nombre: '',
            especie_id: '1',
            genero: 'Macho',
            fecha_nacimiento: '',
            peso: ''
        });
        setShowPetModal(true);
    };

    const handleSavePet = async () => {
        if (!petFormData.nombre) return toast.error('El nombre es obligatorio');
        
        try {
            const response = await axios.post('/backend/api/mascotas/index.php', {
                ...petFormData,
                cliente_id: selectedCliente.id
            });

            if (response.data.success) {
                toast.success('¡Mascota vinculada con éxito!');
                setShowPetModal(false);
                fetchClientes();
            }
        } catch (error) {
            toast.error('Error al registrar mascota');
        }
    };

    return (
        <div className="page-enter p-8 lg:p-12 max-w-[1600px] mx-auto min-h-screen">
            {/* Header / Search */}
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6">
                <div>
                    <h1 className="text-5xl font-black flex items-center gap-4 text-white">
                        <UserPlus className="text-primary-light" size={48} /> 
                        Clientes <span className="text-primary-light italic font-medium">VIP</span>
                    </h1>
                    <p className="text-white/40 mt-2 text-sm lg:text-base font-medium">Gestión avanzada de la base de datos de propietarios</p>
                </div>
                
                <div className="flex w-full md:w-auto gap-4">
                    <button 
                        onClick={() => {
                            const url = `/backend/api/reportes/clientes_pdf.php?token=${localStorage.getItem('token')}`;
                            if ((window.innerWidth < 1024) || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                                toast.success('Abriendo directorio en visor nativo de la tablet... 👥');
                                window.open(url, '_blank');
                            } else {
                                setShowPreview(true);
                            }
                        }}
                        className="glass hover:bg-white/10 flex items-center gap-3 px-6 py-3 rounded-2xl text-white font-bold text-xs uppercase tracking-widest transition-all active:scale-95 border border-white/5 shadow-xl shadow-black/20"
                    >
                        <Eye size={18} className="text-primary-light" />
                        <span className="hidden sm:inline">Vista Previa Directorio</span>
                    </button>

                    {can('Clientes', 'crear') && (
                        <button 
                            onClick={() => { setEditingCliente(null); setShowModal(true); }}
                            className="premium-btn-primary flex items-center gap-3 px-8 shadow-xl shadow-primary/20"
                        >
                            <UserPlus size={20} />
                            <span className="hidden sm:inline">Nuevo Registro</span>
                        </button>
                    )}
                </div>
            </header>

            {/* Listado Principal */}
            <div className="grid grid-cols-1 gap-8">
                <GlassCard padding="0" className="shadow-2xl border-white/5 overflow-hidden min-h-[600px]">
                    <Table 
                        headers={['ID', 'Nombre Completo', 'Contacto Principal', 'Mascotas', 'Estado']}
                        data={clientes.map(c => ({
                            // Preservamos el original para las acciones
                            _raw: c, 
                            id: <span className="text-[10px] font-black text-white/20">#{c.id}</span>,
                            nombre: (
                                <div className="flex flex-col">
                                    <span className="font-bold text-white/90">{c.nombre} {c.apellido}</span>
                                    <span className="text-[10px] text-white/30 uppercase font-black tracking-widest leading-tight">Cliente VIP</span>
                                </div>
                            ),
                            contacto: (
                                <div className="flex flex-col gap-1">
                                    <span className="flex items-center gap-2 text-xs font-semibold text-primary-light"><Phone size={12} /> {c.telefono}</span>
                                    <span className="text-[10px] text-white/30 truncate">{c.email}</span>
                                </div>
                            ),
                            mascotas: (
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2 bg-primary/10 px-3 py-1 rounded-xl border border-primary/20">
                                        <Dog size={16} className="text-primary-light" />
                                        <span className="font-black text-lg text-primary-light">{c.num_mascotas}</span>
                                    </div>
                                    {can('Mascotas', 'crear') && (
                                        <SafeTippy content="Añadir Mascota Rápidamente" animation="scale">
                                            <button 
                                                onClick={() => openPetModal(c)}
                                                className="text-[10px] font-bold uppercase tracking-widest text-secondary hover:text-white transition-colors flex items-center gap-1 group"
                                            >
                                                + Atar <ChevronRight size={10} className="group-hover:translate-x-1 transition-transform" />
                                            </button>
                                        </SafeTippy>
                                    )}
                                </div>
                            ),
                            estado: <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 rounded-full"><div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div><span className="text-[10px] text-emerald-400 font-extrabold uppercase tracking-widest">Activo</span></div>
                        }))}
                        actions={(row) => (
                            <div className="flex gap-2">
                                {can('Clientes', 'editar') && (
                                    <SafeTippy content="Editar Información">
                                        <button onClick={() => handleEdit(row._raw)} className="glass hover:bg-white/10 p-2 rounded-xl text-primary-light transition-all active:scale-95 group">
                                            <Edit2 size={16} />
                                        </button>
                                    </SafeTippy>
                                )}
                                {can('Clientes', 'eliminar') && (
                                    <SafeTippy content="Eliminar Cliente">
                                        <button onClick={() => handleDelete(row._raw.id)} className="glass hover:bg-red-500/10 p-2 rounded-xl text-red-400 transition-all active:scale-95 group">
                                            <Trash2 size={16} />
                                        </button>
                                    </SafeTippy>
                                )}
                            </div>
                        )}
                        emptyMessage="No se encontraron clientes"
                    />
                </GlassCard>
            </div>

            {/* Modales */}
            <Modal show={showModal} onClose={() => setShowModal(false)} title={editingCliente ? 'Actualizar Información' : 'Registrar Nuevo Cliente'}>
                <form className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light">Nombre</label>
                        <input type="text" value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})} className="input-field bg-slate-800/80" placeholder="Ej. Roberto" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light">Apellido</label>
                        <input type="text" value={formData.apellido} onChange={e => setFormData({...formData, apellido: e.target.value})} className="input-field bg-slate-800/80" placeholder="Ej. Rivera" />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light">Correo Electrónico</label>
                        <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="input-field bg-slate-800/80" placeholder="usuario@ejemplo.com" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light">Teléfono Móvil</label>
                        <input type="text" value={formData.telefono} onChange={e => setFormData({...formData, telefono: formatPhone(e.target.value)})} className="input-field bg-slate-800/80" placeholder="(787) 000-0000" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light">Ciudad</label>
                        <input type="text" value={formData.ciudad} onChange={e => setFormData({...formData, ciudad: e.target.value})} className="input-field bg-slate-800/80" placeholder="Ej. San Juan" />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light">Notas</label>
                        <textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} className="input-field h-24 bg-slate-800/80 resize-none" placeholder="Detalles importantes..."></textarea>
                    </div>
                    <button onClick={handleSave} className="md:col-span-2 premium-btn-primary py-4 font-black">GUARDAR DATOS</button>
                </form>
            </Modal>

            <Modal show={showPetModal} onClose={() => setShowPetModal(false)} title={`Vincular Mascota a ${selectedCliente?.nombre}`}>
                <form className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-secondary">Nombre Mascota</label>
                        <input type="text" value={petFormData.nombre} onChange={e => setPetFormData({...petFormData, nombre: e.target.value})} className="input-field bg-slate-800/80" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-secondary">Especie</label>
                        <select value={petFormData.especie_id} onChange={e => setPetFormData({...petFormData, especie_id: e.target.value})} className="input-field bg-slate-800/80">
                            <option value="1">Perro</option>
                            <option value="2">Gato</option>
                            <option value="3">Otro</option>
                        </select>
                    </div>
                    <button onClick={handleSavePet} className="md:col-span-2 premium-btn-primary py-4 font-black bg-secondary">VINCULAR MASCOTA</button>
                </form>
            </Modal>

            {/* Modal Premium de Vista Previa PDF */}
            <Modal
                show={showPreview}
                onClose={() => setShowPreview(false)}
                title="Vista Previa de Directorio (PDF)"
                size="lg"
            >
                <div className="w-full h-[70vh] rounded-2xl overflow-hidden glass border border-white/5 shadow-2xl relative">
                    <iframe 
                        src={`/backend/api/reportes/clientes_pdf.php?token=${localStorage.getItem('token')}`}
                        className="w-full h-full border-0 hidden md:block"
                        title="PDF Preview"
                    ></iframe>

                    {/* Fallback Tablet */}
                    <div className="md:hidden flex flex-col items-center justify-center h-full p-10 text-center">
                        <UserPlus size={48} className="text-primary-light mb-4" />
                        <h3 className="text-lg font-black text-white mb-2">Visor de Clientes</h3>
                        <p className="text-white/40 mb-6 text-xs font-medium">Su dispositivo requiere abrir el directorio en una ventana externa para una visualización correcta.</p>
                        <button 
                            onClick={() => window.open(`/backend/api/reportes/clientes_pdf.php?token=${localStorage.getItem('token')}`, '_blank')}
                            className="premium-btn-primary px-8 py-3 flex items-center gap-2 text-xs"
                        >
                            <Eye size={16} /> VER DIRECTORIO AHORA
                        </button>
                    </div>
                </div>
                <div className="mt-6 flex justify-between items-center bg-white/5 p-4 rounded-2xl border border-white/5">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-500/20 rounded-xl">
                            <FileText size={18} className="text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-widest text-white/40">Formato Estándar</p>
                            <p className="text-xs font-bold text-white">Directorio_Clientes_VIP.pdf</p>
                        </div>
                    </div>
                    <div className="flex gap-4">
                        <button 
                            onClick={() => setShowPreview(false)}
                            className="px-6 py-2 rounded-xl glass hover:bg-white/5 text-[10px] font-black uppercase tracking-widest transition-all"
                        >
                            Cerrar
                        </button>
                        <a 
                            href={`/backend/api/reportes/clientes_pdf.php?token=${localStorage.getItem('token')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="premium-btn-primary px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-primary/20 flex items-center gap-2"
                        >
                            <ArrowRight size={14} /> Abrir Externo
                        </a>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Clientes;
